// Phase 9 R2 — Create inventory item via R1 RPC.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface CreateInventoryItemInput {
  part_number: string;
  brand?: string | null;
  name: string;
  description?: string | null;
  category?: string | null;
  supplier?: string | null;
  supplier_part_number?: string | null;
  unit_cost: number;
  unit_sell_default?: number | null;
  reorder_point?: number;
  reorder_quantity?: number;
  bin_location?: string | null;
  notes?: string | null;
  initial_quantity?: number;
}

export function useCreateInventoryItem() {
  const { activeShop } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateInventoryItemInput) => {
      if (!activeShop?.id) throw new Error('No active shop');
      const { data, error } = await supabase.rpc('create_inventory_item', {
        p_shop_id: activeShop.id,
        p_part_number: input.part_number,
        p_name: input.name,
        p_brand: input.brand ?? undefined,
        p_description: input.description ?? undefined,
        p_category: input.category ?? undefined,
        p_supplier: input.supplier ?? undefined,
        p_supplier_part_number: input.supplier_part_number ?? undefined,
        p_unit_cost: input.unit_cost,
        p_unit_sell_default: input.unit_sell_default ?? undefined,
        p_reorder_point: input.reorder_point ?? 0,
        p_reorder_quantity: input.reorder_quantity ?? 0,
        p_bin_location: input.bin_location ?? undefined,
        p_notes: input.notes ?? undefined,
        p_initial_qty: input.initial_quantity ?? 0,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-items', activeShop?.id] });
      toast.success('Inventory item added');
    },
    onError: (e: any) => toast.error(e?.message ?? 'Failed to add item'),
  });
}
